'use client'

import React, { useEffect, useState } from 'react'
import AnimatedSection from '@/components/AnimatedSection'
import Typography from '@/components/Typography'
import useEmblaCarousel from 'embla-carousel-react'
import styles from './styles.module.css'

// Категория технологии (совпадает с названием таба, кроме «Все»)
type Category = 'Backend' | 'Frontend' | 'QA' | 'DevTools' | 'AI' | 'Другое'

// Табы. «Все» — всегда первый.
const CATEGORIES: string[] = ['Все', 'Backend', 'Frontend', 'QA', 'DevTools', 'AI', 'Другое']

// На мобилке показываем максимум столько иконок, остальные — по кнопке «Показать все»
const MOBILE_LIMIT = 18

interface Tech {
	name: string
	category: Category
	image: string // десктоп / планшет
	imageMobile: string // мобилка (картинки отличаются)
}

// ЗАГЛУШКА: пути mobile-картинок — заменить на свои (сейчас /services/instrument/mobile/img_N.svg)
const technologies: Tech[] = [
	{ name: 'Flutter', category: 'Frontend', image: '/services/instrument/img_1.svg', imageMobile: '/services/instrument/mobile/img_1.svg' },
	{ name: 'TypeScript', category: 'Frontend', image: '/services/instrument/img_2.svg', imageMobile: '/services/instrument/mobile/img_2.svg' },
	{ name: 'React', category: 'Frontend', image: '/services/instrument/img_3.svg', imageMobile: '/services/instrument/mobile/img_3.svg' },
	{ name: 'Angular', category: 'Frontend', image: '/services/instrument/img_4.svg', imageMobile: '/services/instrument/mobile/img_4.svg' },
	{ name: 'React Native', category: 'Frontend', image: '/services/instrument/img_5.svg', imageMobile: '/services/instrument/mobile/img_5.svg' },
	{ name: 'Vue', category: 'Frontend', image: '/services/instrument/img_6.svg', imageMobile: '/services/instrument/mobile/img_6.svg' },
	{ name: 'Symfony', category: 'Backend', image: '/services/instrument/img_7.svg', imageMobile: '/services/instrument/mobile/img_7.svg' },
	{ name: 'SQLite', category: 'Backend', image: '/services/instrument/img_8.svg', imageMobile: '/services/instrument/mobile/img_8.svg' },
	{ name: 'Python', category: 'Backend', image: '/services/instrument/img_9.svg', imageMobile: '/services/instrument/mobile/img_9.svg' },
	{ name: 'PostgreSQL', category: 'Backend', image: '/services/instrument/img_10.svg', imageMobile: '/services/instrument/mobile/img_10.svg' },
	{ name: 'PHP', category: 'Backend', image: '/services/instrument/img_11.svg', imageMobile: '/services/instrument/mobile/img_11.svg' },
	{ name: 'Node.js', category: 'Backend', image: '/services/instrument/img_12.svg', imageMobile: '/services/instrument/mobile/img_12.svg' },
	{ name: 'Laravel', category: 'Backend', image: '/services/instrument/img_13.svg', imageMobile: '/services/instrument/mobile/img_13.svg' },
	{ name: '.NET', category: 'Backend', image: '/services/instrument/img_14.svg', imageMobile: '/services/instrument/mobile/img_14.svg' },
	{ name: 'MySQL', category: 'Backend', image: '/services/instrument/img_15.svg', imageMobile: '/services/instrument/mobile/img_15.svg' },
	{ name: 'Gulp', category: 'DevTools', image: '/services/instrument/img_16.svg', imageMobile: '/services/instrument/mobile/img_16.svg' },
	{ name: 'Git', category: 'DevTools', image: '/services/instrument/img_17.svg', imageMobile: '/services/instrument/mobile/img_17.svg' },
	{ name: 'Docker', category: 'DevTools', image: '/services/instrument/img_18.svg', imageMobile: '/services/instrument/mobile/img_18.svg' },
	{ name: 'CI/CD', category: 'DevTools', image: '/services/instrument/img_19.svg', imageMobile: '/services/instrument/img_19.svg' },
	{ name: 'Webpack', category: 'DevTools', image: '/services/instrument/img_20.svg', imageMobile: '/services/instrument/img_20.svg' },
	{ name: 'Selenium', category: 'QA', image: '/services/instrument/img_21.svg', imageMobile: '/services/instrument/img_21.svg' },
	{ name: 'LambdaTest', category: 'QA', image: '/services/instrument/img_22.svg', imageMobile: '/services/instrument/img_22.svg' },
	{ name: 'Figma', category: 'Другое', image: '/services/instrument/img_23.svg', imageMobile: '/services/instrument/img_23.svg' },
	{ name: 'Agile', category: 'Другое', image: '/services/instrument/img_24.svg', imageMobile: '/services/instrument/img_24.svg' },
	{ name: 'Scrum', category: 'Другое', image: '/services/instrument/img_25.svg', imageMobile: '/services/instrument/img_25.svg' },
	{ name: 'Gemini', category: 'AI', image: '/services/instrument/img_26.svg', imageMobile: '/services/instrument/img_26.svg' },
	{ name: 'Cursor', category: 'AI', image: '/services/instrument/img_27.svg', imageMobile: '/services/instrument/img_27.svg' },
	{ name: 'ChatGPT', category: 'AI', image: '/services/instrument/img_28.svg', imageMobile: '/services/instrument/img_28.svg' },
	{ name: 'GigaChat', category: 'AI', image: '/services/instrument/img_29.svg', imageMobile: '/services/instrument/img_29.svg' },
	{ name: 'Claude', category: 'AI', image: '/services/instrument/img_30.svg', imageMobile: '/services/instrument/img_30.svg' }
]

const Instrument: React.FC = () => {
	const [activeCategory, setActiveCategory] = useState<string>('Все')
	const [showAll, setShowAll] = useState<boolean>(false)
	const [windowWidth, setWindowWidth] = useState<number>(
		typeof window !== 'undefined' ? window.innerWidth : 1440
	)

	const [emblaRef] = useEmblaCarousel({
		dragFree: true,
		align: 'start'
	})

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth)
		handleResize()
		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	// Сбрасываем «показать все» при смене категории
	useEffect(() => {
		setShowAll(false)
	}, [activeCategory])

	const isMobile = windowWidth <= 767

	const filtered =
		activeCategory === 'Все'
			? technologies
			: technologies.filter((tech) => tech.category === activeCategory)

	const visible = isMobile && !showAll ? filtered.slice(0, MOBILE_LIMIT) : filtered
	const showButton = isMobile && !showAll && filtered.length > MOBILE_LIMIT

	// Максимально возможное количество плиток при текущем состоянии — по нему строится
	// каркас-распорка, чтобы высота сетки не менялась при переключении категорий
	const skeletonCount =
		isMobile && !showAll ? Math.min(technologies.length, MOBILE_LIMIT) : technologies.length

	// На мобилке кнопка «Показать все» есть только у категорий длиннее лимита —
	// резервируем её место, иначе секция ниже всё равно прыгает
	const reserveButton = isMobile && !showAll

	return (
		<section className={styles.section}>
			<AnimatedSection animation='slideUp'>
				<div className={styles.heading}>
					<Typography variant='h4' color='accent' className={styles.subtitle}>
						Современные инструменты
					</Typography>
					<Typography variant='h2' className={styles.title}>
						Технологический стек
					</Typography>
				</div>
			</AnimatedSection>

			<AnimatedSection animation='slideUp'>
				<div className={styles.categories}>
					<div className={styles.embla__viewport} ref={emblaRef}>
						<div className={styles.embla__container}>
							{CATEGORIES.map((category) => (
								<div key={category} className={styles.embla__slide}>
									<button
										className={`${styles.categoryButton} ${
											activeCategory === category ? styles.categoryButtonActive : ''
										}`}
										onClick={() => setActiveCategory(category)}
									>
										{category}
									</button>
								</div>
							))}
						</div>
					</div>
				</div>
			</AnimatedSection>

			<AnimatedSection animation='slideUp'>
				<div className={styles.gridWrapper}>
					{/* Каркас-распорка: пустые плитки того же размера задают высоту по самой полной раскладке */}
					<div className={`${styles.grid} ${styles.gridSkeleton}`} aria-hidden='true'>
						{Array.from({ length: skeletonCount }, (_, index) => (
							<div key={index} className={styles.tile} />
						))}
					</div>

					<div className={`${styles.grid} ${styles.gridContent}`}>
						{visible.map((tech) => (
							<div key={tech.name} className={styles.tile}>
								<img src={tech.image} alt={tech.name} className={styles.tileImage} />
								{/* Если mobile-картинки нет (19–30) и на мобилке используется десктопная — подгоняем её через cover-модификатор */}
								<img
									src={tech.imageMobile}
									alt={tech.name}
									className={`${styles.tileImageMobile} ${
										tech.imageMobile === tech.image ? styles.tileImageMobileFallback : ''
									}`}
								/>
							</div>
						))}
					</div>
				</div>
			</AnimatedSection>

			{reserveButton && (
				<button
					className={`${styles.showAllButton} ${showButton ? '' : styles.showAllButtonHidden}`}
					onClick={() => setShowAll(true)}
					aria-hidden={showButton ? undefined : true}
					tabIndex={showButton ? undefined : -1}
				>
					<span className={styles.showAllText}>Показать все</span>
					<svg
						className={styles.showAllIcon}
						width='8'
						height='5'
						viewBox='0 0 8 5'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<path d='M0.94 0L4 3.05333L7.06 0L8 0.94L4 4.94L0 0.94L0.94 0Z' fill='white' />
					</svg>
				</button>
			)}
		</section>
	)
}

Instrument.displayName = 'Instrument'

export default Instrument
